/**
 * How the fan page merges a menu_items realtime event into its list. Pure, so
 * the one rule that matters — an item limited to other tenants
 * (menu_items.tenant_club_ids) leaves the list and the cart like a disabled
 * one — is testable without the 5,000-line page.
 */
export interface FanMenuItemLike {
  id: string;
  sort_order: number;
  enabled: boolean;
  tenant_club_ids?: string[] | null;
  visible_sections?: string[] | null;
}

/** NULL/missing = every tenant; an array = only those; empty = nobody. With no tenant known, keep what the server sent. */
export function isOfferedTo(item: { tenant_club_ids?: string[] | null }, clubId: string | null): boolean {
  if (!Array.isArray(item.tenant_club_ids)) return true;
  if (!clubId) return true;
  return item.tenant_club_ids.includes(clubId);
}

/** Per-section visibility: NULL, missing or empty = every section; unknown seat section = visible. */
export function isVisibleInSection(item: { visible_sections?: string[] | null }, section: string | null | undefined): boolean {
  const list = item.visible_sections;
  if (!list || list.length === 0 || !section) return true;
  return list.includes(section);
}

export function applyMenuItemChange<T extends FanMenuItemLike>(
  items: T[],
  event: 'INSERT' | 'UPDATE',
  changed: T,
  clubId: string | null,
  seatSection: string | null = null,
): { items: T[]; removeFromCart: string | null } {
  const visible = changed.enabled && isOfferedTo(changed, clubId) && isVisibleInSection(changed, seatSection);
  const exists = items.some((i) => i.id === changed.id);
  if (!visible) {
    return { items: exists ? items.filter((i) => i.id !== changed.id) : items, removeFromCart: changed.id };
  }
  const bySort = (a: T, b: T) => a.sort_order - b.sort_order;
  if (exists) {
    return { items: items.map((i) => (i.id === changed.id ? { ...i, ...changed } : i)).sort(bySort), removeFromCart: null };
  }
  void event; // INSERT and UPDATE of an unseen row land the same way
  return { items: [...items, changed].sort(bySort), removeFromCart: null };
}
